import type { UseInfiniteQueryOptions, UseQueryOptions } from '@tanstack/react-query';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { AxiosError } from 'axios';
import { KEYS, repository } from '@repositories/Repository';
import type {
  Filter,
  LicenseCreateDTO,
  LicenseDTO,
  LicenseSubscriptionUserUpdateDTO,
  LicenseUpdateDTO,
  LicenseSubscriptionUserBulkUpdateDTO,
} from '@repositories/licenseRepository/license.types';
import type { ILicenseRepository } from '@repositories/licenseRepository/ILicenseRepository';
import type { LicenseStatus } from '@repositories/subscriptionUserRepository/subscriptionUser.types';
import type { SubscriptionUserDTO } from '@repositories/subscriptionRepository/subscription.types';
import type { Page } from '@type/Page';
import { QueryUtil } from '@utils/queryUtil';
import { LicenseModel } from '@repositories/licenseRepository/models/LicenseModel';
import { SubscriptionUserModel } from '@models/SubscriptionUserModel';
import { sbQueryKey } from './useSubscription';

const licenseRepo = repository.get<ILicenseRepository>(KEYS.LICENSE_REPOSITORY);

const licenseKeys = {
  all: ['license'] as const,
  lists: () => [...licenseKeys.all, 'list'] as const,
  details: () => [...licenseKeys.all, 'detail'] as const,
  list: (queries?: Filter) => [...licenseKeys.lists(), queries] as const,
  listInfinite: (queries: Filter) => [...licenseKeys.list(queries), 'infinite'] as const,
  detail: (id: string) => [...licenseKeys.details(), id] as const,
  userList: (id: string) => [...licenseKeys.lists(), 'user', id] as const,
};

export const useGetLicenseListInfinite = (queries: Filter, options?: UseInfiniteQueryOptions<Page<LicenseModel>>) =>
  useInfiniteQuery({
    queryKey: licenseKeys.listInfinite(queries),
    queryFn: async ({ pageParam = 0 }) => {
      const result = await licenseRepo.getList({ ...queries, page: pageParam });
      return {
        ...result,
        content: result.content.map(dto => new LicenseModel(dto)),
      };
    },
    ...options,
    getNextPageParam: lastPage => (lastPage.last ? undefined : lastPage.number + 1),
  });

export const useGetLicense = (
  id: string,
  options?: Omit<UseQueryOptions<LicenseDTO, AxiosError, LicenseModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectLicense = useCallback(
    (data: LicenseDTO) => new LicenseModel(data),
    [],
  );
  return useQuery<LicenseDTO, AxiosError, LicenseModel>({
    queryKey: licenseKeys.detail(id),
    queryFn: () => licenseRepo.getOne(id),
    select: selectLicense,
    enabled: QueryUtil.withRequired(!!id, options?.enabled),
    ...options,
  });
};

export const useAddLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LicenseCreateDTO) => {
      const result = await licenseRepo.create(data);
      return result;
    },
    onSuccess: created => {
      if (created.subscription?.id) {
        queryClient.invalidateQueries({
          queryKey: licenseKeys.list({
            subscriptionId: created.subscription.id,
          }),
        });
        // NOTE. 라이선스 정보 수정에 따른 구독 기간 변경이 일어날 수 있음.
        queryClient.invalidateQueries({
          queryKey: sbQueryKey.detail(created.subscription.id),
        });
        queryClient.invalidateQueries({ queryKey: sbQueryKey.lists() });
      }
    },
  });
};

export const useUpdateLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LicenseUpdateDTO) => {
      const result = await licenseRepo.update(data.licenseId, data);
      return result;
    },
    onSuccess: updated => {
      if (updated.licenseId && updated.subscription?.id) {
        queryClient.invalidateQueries({
          queryKey: licenseKeys.list({
            subscriptionId: updated.subscription.id,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: licenseKeys.detail(updated.licenseId),
        });
        // NOTE. 라이선스 정보 수정에 따른 구독 기간 변경이 일어날 수 있음.
        queryClient.invalidateQueries({
          queryKey: sbQueryKey.detail(updated.subscription.id),
        });
        queryClient.invalidateQueries({ queryKey: sbQueryKey.lists() });
      }
    },
  });
};

export const useDeleteLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ licenseId, subscriptionId }: { licenseId: string; subscriptionId: string }) => {
      await licenseRepo.delete(licenseId);
      return subscriptionId;
    },
    onSuccess: subscriptionId => {
      queryClient.invalidateQueries({
        queryKey: licenseKeys.list({ subscriptionId }),
      });
    },
  });
};

export const useGetLicenseUserList = (
  id: string,
  subscriptionId: string,
  options?: Omit<UseQueryOptions<SubscriptionUserDTO[], AxiosError, SubscriptionUserModel[]>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectLicenseUsers = useCallback(
    (data: SubscriptionUserDTO[]) => data.map(dto => new SubscriptionUserModel(dto, subscriptionId)),
    [subscriptionId],
  );
  return useQuery<SubscriptionUserDTO[], AxiosError, SubscriptionUserModel[]>({
    queryKey: licenseKeys.userList(id),
    queryFn: () => licenseRepo.getUserList(id),
    select: selectLicenseUsers,
    ...options,
  });
};

export const usePatchLicenseUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ data }: { subscriptionId: string; data: LicenseSubscriptionUserUpdateDTO }) => {
      const result = await licenseRepo.patchUser(data.licenseId, data);
      return result;
    },
    onSuccess: (updated, variables) => {
      const { subscriptionId } = variables;
      if (updated.licenseId) {
        queryClient.invalidateQueries({ queryKey: sbQueryKey.lists() });
        queryClient.invalidateQueries({ queryKey: licenseKeys.lists() });
        queryClient.invalidateQueries({
          queryKey: licenseKeys.list({ subscriptionId }),
        });
        queryClient.invalidateQueries({
          queryKey: licenseKeys.detail(updated.licenseId),
        });
        queryClient.invalidateQueries({
          queryKey: sbQueryKey.detail(subscriptionId),
        });
      }
    },
  });
};

export const useCreateBulkLicenseUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ data }: { subscriptionId: string; data: LicenseSubscriptionUserBulkUpdateDTO }) => {
      const result = await licenseRepo.updateUser(data.licenseId, data);
      return result;
    },
    onSuccess: (updated, variables) => {
      const { subscriptionId } = variables;
      if (updated.licenseId) {
        queryClient.invalidateQueries({ queryKey: sbQueryKey.lists() });
        queryClient.invalidateQueries({ queryKey: licenseKeys.lists() });
        queryClient.invalidateQueries({
          queryKey: licenseKeys.list({ subscriptionId }),
        });
        queryClient.invalidateQueries({
          queryKey: licenseKeys.detail(updated.licenseId),
        });
        queryClient.invalidateQueries({
          queryKey: sbQueryKey.detail(subscriptionId),
        });
      }
    },
  });
};

export const useLicenseStatusUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ licenseId, data }: { licenseId: string; data: LicenseStatus }) => {
      const result = await licenseRepo.updateLicenseStatus(licenseId, data);
      return result;
    },
    onSuccess: updated => {
      if (updated.licenseId && updated.subscription?.id) {
        queryClient.invalidateQueries({
          queryKey: licenseKeys.list({
            subscriptionId: updated.subscription.id,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: licenseKeys.detail(updated.licenseId),
        });
        queryClient.invalidateQueries({
          queryKey: sbQueryKey.detail(updated.subscription.id),
        });
        queryClient.invalidateQueries({ queryKey: sbQueryKey.lists() });
      }
    },
  });
};
